/**
 * Compliance: the traveller's data and their rights over it, in one place
 * under Account.
 *
 * These were spread over Settings (an export button, a statistics box with
 * made-up numbers, account deletion), the profile page (deletion again), and
 * two pages no menu linked to (consents, the GDPR export). The emergency
 * access log was only on the emergency page. Each is now a section here,
 * picked with ?tab=, so a link can open the one it means.
 */

import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { PageBanner } from "@/components/ui/PageBanner";
import { DeleteAccountSection } from "@/components/profile/DeleteAccountSection";
import { GDPRDataExport } from "@/components/gdpr/GDPRDataExport";
import { ConsentManagement } from "@/components/consent/ConsentManagement";
import ConsentSettingsCard from "@/components/settings/ConsentSettingsCard";
import EmergencyAuditLog from "@/components/emergency/EmergencyAuditLog";
import { getTravels } from "@/services/api/travels";
import { medicationService } from "@/services/api/medicationService";
import { fhirApi } from "@/services/api/fhirApi";
import { useAuthStore } from "@/stores/authStore";
import { useI18nStore } from "@/stores/i18nStore";
import type { Translation } from "@/types/i18n";

const TABS = ["statistics", "log", "consents", "export", "delete"] as const;
type ComplianceTab = (typeof TABS)[number];

const TAB_LABEL: Record<ComplianceTab, (t: Translation) => string> = {
  statistics: (t) => t.complianceStatistics,
  log: (t) => t.complianceLog,
  consents: (t) => t.complianceConsents,
  export: (t) => t.complianceExport,
  delete: (t) => t.complianceDeleteAccount,
};

function isComplianceTab(value: string | null): value is ComplianceTab {
  return TABS.includes((value ?? "") as ComplianceTab);
}

/** A count read from the traveller's own data; null when it could not be read. */
function useCount(key: string, read: () => Promise<number>) {
  return useQuery({
    queryKey: ["compliance", "count", key],
    queryFn: read,
    retry: false,
    staleTime: 60_000,
  });
}

const Statistics: React.FC = () => {
  const { t, language } = useI18nStore();
  const user = useAuthStore((state) => state.user);
  const trips = useCount("trips", async () => (await getTravels()).length);
  const medications = useCount(
    "medications",
    async () => (await medicationService.getMedications()).data.total,
  );
  const immunizations = useCount("immunizations", async () => {
    const bundle = await fhirApi.getImmunizations();
    return bundle.total ?? bundle.entry?.length ?? 0;
  });

  const since = user?.createdAt ? new Date(user.createdAt) : null;
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: t.statsMemberSince,
      value:
        since && !Number.isNaN(since.getTime())
          ? since.toLocaleDateString(language, {
              month: "long",
              year: "numeric",
            })
          : "—",
    },
    ...[
      { label: t.statsTrips, query: trips },
      { label: t.statsMedications, query: medications },
      { label: t.statsImmunizations, query: immunizations },
    ].map(({ label, query }) => ({
      label,
      value: query.isLoading ? "…" : query.isError ? "—" : query.data,
    })),
  ];
  const anyFailed =
    trips.isError || medications.isError || immunizations.isError;

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <dl className="divide-y divide-gray-100">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between py-3 text-sm"
          >
            <dt className="text-gray-600">{row.label}</dt>
            <dd className="font-medium text-gray-900">{row.value}</dd>
          </div>
        ))}
      </dl>
      {anyFailed && (
        <p className="mt-3 text-xs text-gray-500">— {t.statsUnavailable}</p>
      )}
    </div>
  );
};

export const CompliancePage: React.FC = () => {
  const { t } = useI18nStore();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab: ComplianceTab = isComplianceTab(requested)
    ? requested
    : "statistics";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageBanner
        icon={ShieldCheck}
        title={t.navCompliance}
        description={t.complianceDescription}
      />

      <nav aria-label={t.navCompliance}>
        <ul className="flex gap-2 overflow-x-auto pb-1">
          {TABS.map((id) => {
            const active = id === tab;
            return (
              <li key={id} className="shrink-0">
                <Link
                  to={`?tab=${id}`}
                  aria-current={active ? "page" : undefined}
                  data-testid={`compliance-tab-${id}`}
                  className={`inline-flex min-h-[40px] items-center whitespace-nowrap rounded-lg px-3 text-sm ${
                    active
                      ? "bg-primary-50 font-bold text-primary-700"
                      : "font-semibold text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {TAB_LABEL[id](t)}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {tab === "statistics" && <Statistics />}
      {tab === "log" && (
        <>
          <p className="text-sm text-gray-600">{t.complianceLogLead}</p>
          <EmergencyAuditLog />
        </>
      )}
      {tab === "consents" && (
        <>
          <ConsentSettingsCard />
          <ConsentManagement />
          <Link
            to="/traveler/consent-requests"
            className="inline-block text-sm font-semibold text-primary-600 hover:underline"
          >
            {t.navConsentRequests} →
          </Link>
        </>
      )}
      {tab === "export" && <GDPRDataExport />}
      {tab === "delete" && <DeleteAccountSection />}
    </div>
  );
};

export default CompliancePage;
